<template>
  <auth-layout :kicker="t.kicker" :title="t.title" :text="t.text" :bullets="bullets">
    <!-- Nach erfolgreicher Registrierung -->
    <div v-if="registeredEmail" class="text-center">
      <v-icon size="56" color="success">mdi-email-check-outline</v-icon>
      <h2 class="auth-card-title mt-4">{{ t.success.title }}</h2>
      <p class="register-text">{{ t.success.text(registeredEmail) }}</p>
      <p class="register-text register-small">{{ t.success.spam }}</p>
      <v-btn x-large depressed color="success" class="mt-2" :to="loginLink">
        {{ t.success.button }}
        <v-icon right>mdi-arrow-right</v-icon>
      </v-btn>
    </div>

    <template v-else>
      <h2 class="auth-card-title">{{ t.cardTitle }}</h2>

      <v-btn-toggle v-model="type" mandatory color="success" class="auth-type">
        <v-btn v-for="(option, key) in types" :key="key" :value="key" :disabled="submitting">
          <v-icon left small>{{ option.icon }}</v-icon>
          {{ option.label }}
        </v-btn>
      </v-btn-toggle>

      <v-alert v-if="serverError" type="error" text dense class="mb-6">{{ serverError }}</v-alert>
      <v-alert v-if="formError" type="warning" text dense class="mb-6">{{ formError }}</v-alert>

      <v-form ref="form" @submit.prevent="submit">
        <v-text-field
          v-model="form.email"
          :label="isClub ? t.fields.clubEmail : t.fields.email"
          :rules="[rules.required(t.required.email), rules.email]"
          :error-messages="serverErrors.email"
          type="email"
          autocomplete="email"
          outlined
          @input="clearServerError('email')"
        ></v-text-field>

        <v-row v-if="!isClub" dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.vorname"
              :label="t.fields.vorname"
              :rules="[rules.required(t.required.vorname)]"
              :error-messages="serverErrors.vorname"
              autocomplete="given-name"
              outlined
              @input="clearServerError('vorname')"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.name"
              :label="t.fields.name"
              :rules="[rules.required(t.required.name)]"
              :error-messages="serverErrors.name"
              autocomplete="family-name"
              outlined
              @input="clearServerError('name')"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-text-field
          v-if="!isClub"
          v-model="form.mitgliedsnr"
          :label="t.fields.mitgliedsnr"
          :hint="t.fields.mitgliedsnrHint"
          :error-messages="serverErrors.mitgliedsnr"
          persistent-hint
          outlined
          class="mb-2"
          @input="clearServerError('mitgliedsnr')"
        ></v-text-field>

        <template v-if="isClub">
          <v-text-field
            v-model="form.name"
            :label="t.fields.clubName"
            :rules="[rules.required(t.required.clubName)]"
            :error-messages="serverErrors.name"
            autocomplete="organization"
            outlined
            @input="clearServerError('name')"
          ></v-text-field>
          <v-text-field
            v-model="form.vereinsnr"
            :label="t.fields.vereinsnr"
            :hint="t.fields.vereinsnrHint"
            :rules="[rules.required(t.required.vereinsnr)]"
            :error-messages="serverErrors.vereinsnr"
            persistent-hint
            outlined
            class="mb-2"
            @input="clearServerError('vereinsnr')"
          ></v-text-field>
        </template>

        <v-row dense class="mt-2">
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.password"
              :label="t.fields.password"
              :rules="[rules.required(t.required.password), passwordRule]"
              :error-messages="serverErrors.password"
              :type="showPassword ? 'text' : 'password'"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              autocomplete="new-password"
              outlined
              @click:append="showPassword = !showPassword"
              @input="clearServerError('password')"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.password2"
              :label="t.fields.password2"
              :rules="[v => v === form.password || t.passwordMismatch]"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="new-password"
              outlined
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="register-rules">
          <span class="register-small">{{ t.passwordRules.title }}</span>
          <ul>
            <li v-for="rule in passwordChecks" :key="rule.key" :class="{ 'register-rule--ok': rule.ok }">
              <v-icon x-small :color="rule.ok ? 'success' : 'grey'">
                {{ rule.ok ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              {{ rule.text }}
            </li>
          </ul>
        </div>

        <div class="register-captcha">
          <vue-hcaptcha
            ref="captcha"
            sitekey="c54eaec0-f39c-4d44-9388-c74ea76c22ec"
            @verify="onVerify"
            @expired="onExpire"
            @challengeExpired="onExpire"
            @error="onExpire"
          />
          <div v-if="captchaError" class="error--text register-small mt-1">{{ captchaError }}</div>
        </div>

        <v-btn type="submit" block x-large depressed color="success" :loading="submitting" :disabled="submitting">
          {{ submitting ? t.submitting : t.submit }}
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>

        <p class="register-small register-privacy">
          {{ t.privacy.text }}
          <router-link :to="t.privacy.to" class="auth-link">{{ t.privacy.link }}</router-link>.
        </p>
      </v-form>

      <p class="auth-switch">
        {{ t.switch.text }}
        <router-link :to="t.switch.to" class="auth-link">{{ t.switch.link }}</router-link>
      </p>
    </template>
  </auth-layout>
</template>

<script>
import authPages from '@/content/authPages';
import publicPages from '@/content/publicPages';
import AuthLayout from './auth/AuthLayout.vue';

const emptyForm = () => ({
  email: '',
  vorname: '',
  name: '',
  mitgliedsnr: '',
  vereinsnr: '',
  password: '',
  password2: ''
});

export default {
  name: 'register',

  components: { AuthLayout },

  data() {
    const t = authPages.register;
    return {
      t,
      types: authPages.types,
      type: this.$route.query.type === 'club' ? 'club' : 'user',
      form: emptyForm(),
      showPassword: false,
      token: null,
      submitting: false,
      // Fehler vom Backend (422), je Feld: { email: ['…'], … }
      serverErrors: {},
      formError: '',
      serverError: '',
      captchaError: '',
      registeredEmail: '',
      rules: {
        required: message => v => !!(v && String(v).trim()) || message,
        email: v => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) || t.emailFormat
      }
    };
  },

  computed: {
    isClub() {
      return this.type === 'club';
    },
    bullets() {
      const audience = publicPages.landing.audiences.find(a => a.id === (this.isClub ? 'vereine' : 'schuetzen'));
      return audience ? audience.items : [];
    },
    passwordChecks() {
      const p = this.form.password;
      const r = this.t.passwordRules;
      return [
        { key: 'length', text: r.length, ok: p.length >= 10 },
        { key: 'mixedCase', text: r.mixedCase, ok: /[a-zäöüß]/.test(p) && /[A-ZÄÖÜ]/.test(p) },
        { key: 'number', text: r.number, ok: /\d/.test(p) },
        { key: 'symbol', text: r.symbol, ok: /[^A-Za-z0-9äöüÄÖÜß\s]/.test(p) }
      ];
    },
    passwordRule() {
      return () => this.passwordChecks.every(rule => rule.ok) || this.t.passwordRules.notMet;
    },
    loginLink() {
      return { path: '/login', query: { email: this.registeredEmail, type: this.type } };
    }
  },

  watch: {
    type() {
      this.serverErrors = {};
      this.formError = '';
      if (this.$refs.form) this.$refs.form.resetValidation();
    }
  },

  methods: {
    async submit() {
      this.formError = '';
      this.serverError = '';
      this.captchaError = '';
      const formValid = this.$refs.form.validate();
      if (!this.token) this.captchaError = this.t.captchaMissing;
      if (!formValid || !this.token) return;

      const f = this.form;
      const data = this.isClub
        ? { email: f.email.trim(), password: f.password, name: f.name.trim(), vereinsnr: f.vereinsnr.trim() }
        : {
            email: f.email.trim(),
            password: f.password,
            vorname: f.vorname.trim(),
            name: f.name.trim(),
            mitgliedsnr: f.mitgliedsnr.trim()
          };
      data.hcaptcha_token = this.token;

      this.submitting = true;
      try {
        await this.$store.dispatch(this.isClub ? 'ClubAuth/registerClub' : 'UserAuth/registerUser', data);
        this.registeredEmail = data.email;
      } catch (error) {
        this.handleError(error);
        // Ein hCaptcha-Token gilt nur für einen Versuch
        this.resetCaptcha();
      } finally {
        this.submitting = false;
      }
    },

    handleError(error) {
      const response = error && error.response;
      // Ohne Antwort hat der Axios-Interceptor schon „Server nicht erreichbar“ gemeldet
      if (!response) return;

      if (response.status === 422 && response.data && response.data.errors) {
        const { hcaptcha_token: captcha, ...fields } = response.data.errors;
        this.serverErrors = fields;
        this.formError = this.t.formError;
        if (captcha) this.captchaError = captcha[0];
        return;
      }
      if (response.status === 400) {
        this.captchaError = this.t.captchaFailed;
        return;
      }
      // handleRegistrationError zeigt bei einer Antwort ohne errors/error/warning (z. B. Laravel-500) nichts an
      this.serverError = this.t.serverError(publicPages.site.contactMail);
    },

    clearServerError(field) {
      if (this.serverErrors[field]) this.$delete(this.serverErrors, field);
    },

    onVerify(token) {
      this.token = token;
      this.captchaError = '';
    },
    onExpire() {
      this.token = null;
    },
    resetCaptcha() {
      this.token = null;
      if (this.$refs.captcha) this.$refs.captcha.reset();
    }
  }
};
</script>

<style scoped>
.register-text {
  color: #526255;
  line-height: 1.6;
}

.register-small {
  color: #526255;
  font-size: 0.85rem;
}

.register-rules {
  margin: -4px 0 20px;
}

.register-rules ul {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin: 4px 0 0;
  padding: 0 !important;
  list-style: none;
  color: #8a968c;
  font-size: 0.85rem;
}

.register-rule--ok {
  color: #2e7d32;
}

.register-captcha {
  margin-bottom: 20px;
}

.register-privacy {
  margin: 12px 0 0;
  text-align: center;
}
</style>
